import { FileOrFolder } from "../../../types/FileOrFolder";
import api from "../../axiosSetup";
import { createSlice, PayloadAction, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";

interface BCRInspectState {
    bcrFile: FileOrFolder | null;
    supportingDocs: FileOrFolder[];
    folderRoot: string;
    signatureDoc: FileOrFolder | null;
    initLoading: boolean;
    error: string | null;
}

const initialState: BCRInspectState = {
    bcrFile: null,
    signatureDoc: null,
    folderRoot: '',
    supportingDocs: [],
    initLoading: false,
    error: null,
}

const bcrInspectSlice = createSlice({
    name: "bcrInspect",
    initialState,
    reducers: {
        setBcrFileIfCurrent: (
          state,
          action: PayloadAction<{
            file: FileOrFolder | null;
            mode?: string;
          }>
        ) => {
          const { file, mode } = action.payload;

          if (!file) return;
          if (!state.bcrFile) return;

          const currentFileId = String(state.bcrFile._id || "");
          const nextFileId = String(file._id || "");

          const currentBcrId = String(state.bcrFile.bcrContent?._id || "");
          const nextBcrId = String(file.bcrContent?._id || "");

          const isSameBcr =
            currentFileId === nextFileId ||
            (!!currentBcrId && currentBcrId === nextBcrId);

          if (!isSameBcr) return;

          const preserveXY = mode === "search" || mode === "meeting-minutes";

          if (preserveXY) {
            state.bcrFile = {
              ...file,
              x: state.bcrFile.x,
              y: state.bcrFile.y,
            };
            return;
          }

          state.bcrFile = file;
        },
        // setBcrFile: (state, action: PayloadAction<FileOrFolder | null>) => {
        //     state.bcrFile = action.payload;
        // },
        setBcrFile: (
          state,
          action: PayloadAction<{
            file: FileOrFolder | null;
            mode?: string; // OPTIONAL!
          }>
        ) => {
          const { file, mode } = action.payload;

          if (!file) {
            state.bcrFile = null;
            return;
          }
          const preserveXY = mode === "search" || mode === "meeting-minutes";
          if (preserveXY && state.bcrFile) {
            state.bcrFile = {
              ...file,
              x: state.bcrFile.x,
              y: state.bcrFile.y,
            };
            return;
          }
          state.bcrFile = file;
        },
        setBcrFileViaBcrId: (state, action: PayloadAction<FileOrFolder | null>) => {
            state.bcrFile = action.payload;
        },
        setSignedBCR(state, action: PayloadAction<{ signedFileId: string }>) {
            const bcrFile = state.bcrFile;
            const bcrContent = bcrFile?.bcrContent;
            const supportingDocs = bcrContent?.supportingDocs;

            if (!bcrFile || !bcrContent || !supportingDocs) return;

            // Find the signed file
            const signedFile = supportingDocs.find(doc => doc._id === action.payload.signedFileId);
            const docBCR = signedFile?.attachDocBCR;

            // Update attachSignedBCR on all supportingDocs
            bcrContent.supportingDocs = supportingDocs.map(doc => ({
                ...doc,
                attachSignedBCR: doc._id === signedFile?._id ? docBCR : null,
            }));
        },
        removeSignedBCR(state, action: PayloadAction<{ fileId: string }>) {
            const bcrFile = state.bcrFile;
            const bcrContent = bcrFile?.bcrContent;
            const supportingDocs = bcrContent?.supportingDocs;

            if (!bcrFile || !bcrContent || !supportingDocs) return;

            // bcrContent.supportingDocs = supportingDocs.map(doc => ({
            //     ...doc,
            //     attachSignedBCR: doc._id === action.payload.fileId ? null : doc.attachSignedBCR,
            // }));
            // bcrContent.fullySignedDocument = null;
        },
        removeSupportingDocFromBCR: (
          state,
          action: PayloadAction<{ docId: string }>
        ) => {
          if (!state.bcrFile?.bcrContent?.supportingDocs) return;

          // Find the doc to be removed
          const removedDoc = state.bcrFile.bcrContent.supportingDocs.find(
            (doc) => doc._id === action.payload.docId
          );

          // Remove the doc from supportingDocs
          state.bcrFile.bcrContent.supportingDocs = state.bcrFile.bcrContent.supportingDocs.filter(
            (doc) => doc._id !== action.payload.docId
          );

          // If it's a signature doc and userSigned is valid, reset the participant's signature fields
          // const signature = removedDoc?.singleUploadedBCRSignature;
          // const userSignedId = typeof signature?.userSigned === 'string' ? signature.userSigned : signature?.userSigned?._id;
          // if (signature && userSignedId) {
          //   const participant = state.bcrFile.bcrContent.participantUsers.find(
          //     (p) => {
          //       if (typeof p.user === 'string') {
          //         return p.user === userSignedId;
          //       } else if (typeof p.user === 'object' && p.user !== null) {
          //         return p.user._id === userSignedId;
          //       }
          //     }
          //   );

          //   if (participant) {
          //     participant.isSigned = false;
          //     participant.signatureDate = null;
          //     participant.signatureType = null;
          //     participant.extractedSignatureFile = null;
          //   }
          // }
        },
        addSupportingDocToBCR: (state, action: PayloadAction<FileOrFolder>) => {
            if (state.bcrFile?.bcrContent?.supportingDocs) {
                state.bcrFile.bcrContent.supportingDocs.push(action.payload);
            }
        },
        replaceBCRSupportingDocs: (state, action: PayloadAction<{ newDocs: FileOrFolder[] }>) => {
          const { newDocs } = action.payload;
          
          if (state.bcrFile?.bcrContent?.supportingDocs) {
            state.bcrFile.bcrContent.supportingDocs = newDocs
          }
        },
        updateSupportingDocInBCR: (state, action: PayloadAction<{ supportingDoc: FileOrFolder, signaturesMergedImg: string }>) => {
          const { supportingDoc, signaturesMergedImg } = action.payload;

          if (state.bcrFile?.bcrContent?.supportingDocs) {
            const index = state.bcrFile.bcrContent.supportingDocs.findIndex(
              (doc) => doc._id === supportingDoc._id
            );

            if (index !== -1) {
              // ✅ Replace the existing document
              state.bcrFile.bcrContent.supportingDocs[index] = supportingDoc;
            } else {
              // Optional: fallback to push if not found
              state.bcrFile.bcrContent.supportingDocs.push(supportingDoc);
            }
            
            //Signed DocuSign Doc
           if (state.bcrFile && state.bcrFile.bcrContent) {
                state.bcrFile.bcrContent.signaturesMergedImg = signaturesMergedImg;
            }
          }
        },
        addSignedDocToBCR: (state, action: PayloadAction<FileOrFolder>) => {
            const bcrFile = state.bcrFile;
            const bcrContent = bcrFile?.bcrContent;

            if (!bcrFile || !bcrContent) return;

            // Ensure supportingDocs array exists
            if (!bcrContent.supportingDocs) {
                bcrContent.supportingDocs = [];
            }

            // Add the new signed doc to supportingDocs
            bcrContent.supportingDocs.push(action.payload);

            // Get the _id of the new signed doc and its attachDocBCR reference
            const newSignedId = action.payload._id;
            const docBCR = action.payload.attachDocBCR;

            // Update all supportingDocs: only the new doc keeps attachSignedBCR, others set to null
            bcrContent.supportingDocs = bcrContent.supportingDocs.map(doc => ({
                ...doc,
                attachSignedBCR: doc._id === newSignedId ? docBCR : null,
            }));

            // Set the top-level fullySignedDocument reference
            // bcrContent.fullySignedDocument = {
            //     ...action.payload,
            //     attachSignedBCR: docBCR,
            // };
        },
        updateBCRFile: (
          state,
          action: PayloadAction<{ file: FileOrFolder; signaturesMergedImg?: string }>
        ) => {
          const { file, signaturesMergedImg } = action.payload;

          if (!state.bcrFile || !state.bcrFile.bcrContent) {
            state.bcrFile = file;
            if (signaturesMergedImg && state.bcrFile?.bcrContent) {
              state.bcrFile.bcrContent.signaturesMergedImg = signaturesMergedImg;
            }
            return;
          }

          const existingSupportingDocs = state.bcrFile.bcrContent.supportingDocs ?? [];
          const newContent = file.bcrContent;
          if (!newContent) return;

          state.bcrFile = {
            ...file,
            bcrContent: {
              ...newContent,
              _id: newContent._id || '',
              title: newContent.title || '',
              departmentName: newContent.departmentName || '',
              dulyConvenedMeeting: newContent.dulyConvenedMeeting || new Date(),
              // whereAsPurpose: newContent.whereAsPurpose || '',
              // whereAsBackground: newContent.whereAsBackground || '',
              // whereAsBeItResolved: newContent.whereAsBeItResolved || '',
              whereAsClauses: newContent.whereAsClauses || [],
              beItResolvedClauses: newContent.beItResolvedClauses || [],
              resolutionNumber: newContent.resolutionNumber || '',
              quorum: newContent.quorum || 0,
              // isFinished: newContent.isFinished ?? false,
              participantUsers: newContent.participantUsers || [],
              reserve: newContent.reserve || '',
              // fullySignedDocument: newContent.fullySignedDocument ?? null,
              signaturesMergedImg: signaturesMergedImg ?? newContent.signaturesMergedImg ?? null,
              supportingDocs: existingSupportingDocs,
            },
          };
        },
        renameSupportingFile: (state, action: PayloadAction<{ docId: string, newName: string }>) => {
            const { docId, newName } = action.payload;
            const bcrFile = state.bcrFile;
            const bcrContent = bcrFile?.bcrContent;

            if (!bcrFile || !bcrContent) return;

            // Ensure supportingDocs array exists
            if (!bcrContent.supportingDocs) {
                bcrContent.supportingDocs = [];
            }

            bcrContent.supportingDocs = bcrContent.supportingDocs.map(doc => ({
                ...doc,
                name: doc._id === docId ? newName : doc.name,
            }));

            // if (bcrContent.fullySignedDocument && typeof bcrContent.fullySignedDocument === 'object' && bcrContent.fullySignedDocument?._id! === docId) {
            //     bcrContent.fullySignedDocument.name = newName;
            // }
        },
        updateParticipantUsers: (state, action: PayloadAction<any[]>) => {
            if (state.bcrFile?.bcrContent) {
                state.bcrFile.bcrContent.participantUsers = action.payload;
            }
        },
        setUploadedSignature: ( //Does both participant user AND uploads signaturesMergedImg in realtime (/manage-bcr)
          state,
          action: PayloadAction<{ userId: string; signatureDate: Date; signaturesMergedImg: string; }>
      ) => {
        const { userId, signatureDate, signaturesMergedImg } = action.payload;

        const participant = state.bcrFile?.bcrContent?.participantUsers?.find(
          p => typeof p.user !== 'string' && p.user?._id === userId
        );

        if (participant) {
          participant.isSigned = true;
          participant.signatureDate = signatureDate;
          participant.signatureType = "Manual";
        }

        if (signaturesMergedImg && state.bcrFile?.bcrContent) {
          state.bcrFile.bcrContent.signaturesMergedImg = signaturesMergedImg;
        }
      },
      // Removes '{nameInitials} Signed' above file that's being turned into E-Signature
      clearUploadedSignatureFromDoc: (
        state,
        action: PayloadAction<{ fileId: string }>
      ) => {
        const fileId = action.payload.fileId;
        const targetDoc = state.bcrFile?.bcrContent?.supportingDocs?.find(
          doc => doc._id === fileId
        );
        // if (targetDoc?.singleUploadedBCRSignature) {
        //   targetDoc.singleUploadedBCRSignature.userSigned = null;
        //   targetDoc.singleUploadedBCRSignature.userName = null;
        //   targetDoc.singleUploadedBCRSignature.extractedSignatureImg = null;
        // }
      }
    },
    extraReducers: (builder) => {
      builder
      //Fetch BCR File
      .addCase(fetchBcrFile.pending, (state) => {
            state.initLoading = true;
            state.error = null;
        })
        .addCase(fetchBcrFile.fulfilled, (state, action: PayloadAction<FileOrFolder>) => {
            state.initLoading = false;
            state.bcrFile = action.payload;
            console.log('bcrFile', action.payload);
        })
        .addCase(fetchBcrFile.rejected, (state, action) => {
            state.initLoading = false;
            state.error = action.payload || 'Failed to fetch BCR file';
        })
        //Fetch BCR File - NO loading (for DocuSign refetching)
        // .addCase(fetchBcrFileNoLoading.fulfilled, (state, action: PayloadAction<FileOrFolder>) => {
        //     state.bcrFile = action.payload;
        // })
        .addCase(fetchBcrFileNoLoading.fulfilled, (state, action) => {
        // .addCase(refreshBcrFileIfCurrent.fulfilled, (state, action) => {
          const file = action.payload;

          if (!state.bcrFile) return;

          const currentFileId = String(state.bcrFile._id || "");
          const nextFileId = String(file._id || "");

          const currentBcrId = String(state.bcrFile.bcrContent?._id || "");
          const nextBcrId = String(file.bcrContent?._id || "");

          const isSameBcr =
            currentFileId === nextFileId ||
            (!!currentBcrId && currentBcrId === nextBcrId);

          if (!isSameBcr) return;

          state.bcrFile = file;
        })
    }
})

// export const fetchSupportingDocs = createAsyncThunk(
//     "fileExplorer/fetchSupportingDocs",
//     async (_, { getState, rejectWithValue }) => {
//         const { bcrInspectReducer } = getState() as { bcrInspectReducer: BCRInspectState };
//         return true;
//         // try {
//         //     const response = await api.get(`/api/files-folders`);
//         //     return response.data.items;
//         // } catch (err) {
//         //     const error = err as AxiosError<{ message: string }>;
//         //     return rejectWithValue(error.response?.data?.message || "Error fetching items");
//         // }
//     }
// )

export const fetchBcrFile = createAsyncThunk<
  FileOrFolder,              // Return type
  string,                    // Argument type (the BCR file ID)
  { rejectValue: string }    // Optional error type
>(
  'bcrInspect/fetchBcrFile',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/bcrs-file/${id}`);
      return response.data.file as FileOrFolder;
    } catch (err) {
      const error = err as AxiosError<{ message: string }>;
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch BCR file');
    }
  }
);

export const fetchBcrFileNoLoading = createAsyncThunk<
  FileOrFolder,              // Return type
  string,                    // Argument type (the BCR file ID)
  { rejectValue: string }    // Optional error type
>(
  'bcrInspect/fetchBcrFileNoLoading',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/bcrs-file/${id}`);
      return response.data.file as FileOrFolder;
    } catch (err) {
      const error = err as AxiosError<{ message: string }>;
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch BCR file');
    }
  }
);

export const { 
  setBcrFile,
  setBcrFileIfCurrent,
  setSignedBCR,
  removeSignedBCR,
  removeSupportingDocFromBCR,
  addSupportingDocToBCR,
  updateSupportingDocInBCR,
  addSignedDocToBCR,
  updateBCRFile,
  renameSupportingFile,
  updateParticipantUsers,
  setUploadedSignature,
  clearUploadedSignatureFromDoc,
  replaceBCRSupportingDocs
    } = bcrInspectSlice.actions;

export default bcrInspectSlice.reducer;

// function findFolderById(folderId: string, itemsList: FileOrFolder[]): FileOrFolder | undefined {
//     for (const item of itemsList) {
//         if (item._id === folderId) {
//             return item; // Found in the main list
//         }

//         // If item has children, search inside them recursively
//         if (item.children && item.children.length > 0) {
//             const foundInChildren = findFolderById(folderId, item.children);
//             if (foundInChildren) return foundInChildren; // Return if found
//         }
//     }
//     return undefined; // Not found
// }
